// useHashRouter — lekkie routing bez react-router.
// Routes jako literały hash'a (np. "#/tasks?from=2026-04-13&to=2026-04-19").
//
// API:
//   const { path, query, navigate, back } = useHashRouter();
//   - path: string (np. "/tasks", "/address/recXYZ/floor/EG")
//   - query: Object (params z ?)
//   - navigate(path, query?)  — push new hash
//   - back()                   — window.history.back

import { useState, useEffect, useCallback } from 'react';

function parseHash() {
  const raw = (window.location.hash || '#/').slice(1); // drop '#'
  const [pathPart, queryPart = ''] = raw.split('?');
  const path = pathPart || '/';
  const query = {};
  if (queryPart) {
    for (const [k, v] of new URLSearchParams(queryPart).entries()) {
      query[k] = v;
    }
  }
  return { path, query };
}

export function useHashRouter() {
  const [{ path, query }, setState] = useState(parseHash);

  useEffect(() => {
    const onHashChange = () => setState(parseHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const navigate = useCallback((newPath, newQuery) => {
    let hash = `#${newPath}`;
    if (newQuery && Object.keys(newQuery).length) {
      const qs = new URLSearchParams(newQuery).toString();
      if (qs) hash += `?${qs}`;
    }
    window.location.hash = hash;
  }, []);

  const back = useCallback(() => window.history.back(), []);

  return { path, query, navigate, back };
}

/**
 * Match helper — match "/address/:id/floor/:floor" vs current path.
 * Zwraca params object lub null.
 *
 * @param {string} pattern - "/address/:id"
 * @param {string} path
 * @returns {Object|null}
 */
export function matchPath(pattern, path) {
  const patSegs = pattern.split('/').filter(Boolean);
  const pathSegs = path.split('/').filter(Boolean);
  if (patSegs.length !== pathSegs.length) return null;
  const params = {};
  for (let i = 0; i < patSegs.length; i++) {
    if (patSegs[i].startsWith(':')) {
      params[patSegs[i].slice(1)] = decodeURIComponent(pathSegs[i]);
    } else if (patSegs[i] !== pathSegs[i]) {
      return null;
    }
  }
  return params;
}
